import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  BOARD_LABELS,
  BOARD_DUE_DATE_FILTERS,
} from "../../../app/utils/projectBoard/boardDesignTokens";
import { getCustomLabels } from "../../../app/utils/projectBoard/boardCustomLabels";
import { canEditCardContent, canDragCardInList, canEmployeeMoveCardBetweenLists, canUserAddCardToList } from "../../../app/utils/projectBoard/boardCardPermissions";
import {
  requiresActualHoursForMove,
  missingEstimatedHoursForMove,
  validateEmployeeCardMove,
  validateCardMove,
  isInProgressList,
} from "../../../app/utils/projectBoard/boardListRules";
import {
  canCreateProjectList,
  canManageProjectCard,
  canUpdateProjectBoard,
  canDeleteProjectBoard,
} from "../../../app/utils/projectBoard/projectBoardPermissions";
import { filterBoardCards } from "../../../app/utils/projectBoard/boardFilters";
import {
  applyCardDrag,
  applyListDrag,
  buildListMovePayload,
  buildMovePayload,
  getBoardListsDroppableId,
} from "../../../app/utils/projectBoard/boardDragHelpers";
import {
  fetchBoardById,
  createCardApi,
  createListApi,
  updateListApi,
  fetchCardComments,
  updateCardApi,
  deleteCardApi,
  deleteBoardApi,
  moveCardApi,
  moveBoardListsApi,
  addCardCommentApi,
  fetchCardAttachments,
  uploadCardAttachmentApi,
  isValidBoardId,
} from "../../../app/utils/projectBoard/projectBoardApi";
import { showErrorToast, showSuccessToast } from "../../../app/utils/common/Toast";
import Loader from "../../atoms/Loader/Loader";
import ConfirmModal from "../../atoms/ConfirmModal/ConfirmModal";
import {
  BoardButton,
  BoardChip,
  BoardSearchInput,
  BoardAvatarStack,
  BoardEmptyState,
  BoardIconButton,
} from "../../atoms/BoardDesignSystem";
import BoardListColumn from "../../molecules/BoardListColumn/BoardListColumn";
import BoardCardModal from "../../molecules/BoardCardModal/BoardCardModal";
import BoardActualHoursModal from "../../molecules/BoardActualHoursModal/BoardActualHoursModal";
import BoardEstimatedHoursModal from "../../molecules/BoardEstimatedHoursModal/BoardEstimatedHoursModal";
import BoardActivityFeed from "../../molecules/BoardActivityFeed/BoardActivityFeed";
import BoardAttachmentViewer from "../../molecules/BoardAttachmentViewer/BoardAttachmentViewer";
import BoardCreateListForm from "../../molecules/BoardCreateListForm/BoardCreateListForm";
import BoardCreateModal from "../../molecules/BoardCreateModal/BoardCreateModal";
import BoardDropdownPortal from "../../molecules/BoardDropdownPortal/BoardDropdownPortal";
import "./ProjectBoardPage.scss";

const ProjectBoardPage = () => {
  const { boardId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth.auth);
  const canCreateList = canCreateProjectList();
  const canUpdate = canUpdateProjectBoard();
  const canDeleteBoard = canDeleteProjectBoard();
  const canEditCard = canEditCardContent();
  const canManageCard = canManageProjectCard();

  const [boardData, setBoardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [labelFilter, setLabelFilter] = useState("");
  const [assigneeFilter, setAssigneeFilter] = useState("");
  const [dueFilter, setDueFilter] = useState("");
  const [labelMenuOpen, setLabelMenuOpen] = useState(false);
  const [dueMenuOpen, setDueMenuOpen] = useState(false);
  const [assigneeMenuOpen, setAssigneeMenuOpen] = useState(false);
  const [activityMenuOpen, setActivityMenuOpen] = useState(false);
  const [boardMenuOpen, setBoardMenuOpen] = useState(false);
  const [editBoardOpen, setEditBoardOpen] = useState(false);
  const [deleteBoardOpen, setDeleteBoardOpen] = useState(false);
  const [deletingBoard, setDeletingBoard] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const [cardComments, setCardComments] = useState([]);
  const [isAddingList, setIsAddingList] = useState(false);
  const [addingList, setAddingList] = useState(false);
  const [previewAttachment, setPreviewAttachment] = useState(null);
  const [pendingCardMove, setPendingCardMove] = useState(null);
  const [estimatedHoursModalOpen, setEstimatedHoursModalOpen] = useState(false);
  const [actualHoursModalOpen, setActualHoursModalOpen] = useState(false);
  const [movingCard, setMovingCard] = useState(false);
  const [savingEstimatedHours, setSavingEstimatedHours] = useState(false);
  const labelMenuRef = useRef(null);
  const dueMenuRef = useRef(null);
  const assigneeMenuRef = useRef(null);
  const labelMenuPanelRef = useRef(null);
  const dueMenuPanelRef = useRef(null);
  const assigneeMenuPanelRef = useRef(null);
  const activityMenuRef = useRef(null);
  const activityMenuPanelRef = useRef(null);
  const boardMenuRef = useRef(null);
  const boardMenuPanelRef = useRef(null);
  const boardSnapshotRef = useRef(null);
  const listsSnapshotRef = useRef(null);
  const blockCardClickRef = useRef(false);
  const boardCanvasRef = useRef(null);
  const dragScrollRafRef = useRef(null);
  const dragPointerRef = useRef({ x: 0, cleanup: null });

  useEffect(() => {
    return () => {
      document.body.classList.remove("pb-dnd-active");
      dragPointerRef.current.cleanup?.();
      if (dragScrollRafRef.current) {
        cancelAnimationFrame(dragScrollRafRef.current);
      }
    };
  }, []);

  const loadBoard = useCallback(async (silent = false) => {
    if (!isValidBoardId(boardId)) {
      if (!silent) setBoardData(null);
      return;
    }

    if (!silent) setLoading(true);
    try {
      const data = await fetchBoardById(boardId);
      setBoardData(data);
      return data;
    } catch (error) {
      const msg = error?.response?.data?.error?.message || error?.message || "Failed to load board.";
      showErrorToast(msg);
      if (!silent) setBoardData(null);
      return null;
    } finally {
      if (!silent) setLoading(false);
    }
  }, [boardId]);

  useEffect(() => {
    if (!isValidBoardId(boardId)) {
      showErrorToast("Invalid board link.");
      navigate("/project-management", { replace: true });
      return;
    }
    loadBoard();
  }, [boardId, loadBoard, navigate]);

  useEffect(() => {
    const cardId = searchParams.get("card");
    if (!cardId || !boardData?.cards?.length) return;
    const card = boardData.cards.find((c) => c.cardId === cardId);
    if (card) setSelectedCard(card);
  }, [searchParams, boardData]);

  useEffect(() => {
    const isOutside = (anchorRef, panelRef, target) =>
      !anchorRef.current?.contains(target) && !panelRef.current?.contains(target);

    const onDocClick = (e) => {
      if (isOutside(labelMenuRef, labelMenuPanelRef, e.target)) {
        setLabelMenuOpen(false);
      }
      if (isOutside(dueMenuRef, dueMenuPanelRef, e.target)) {
        setDueMenuOpen(false);
      }
      if (isOutside(assigneeMenuRef, assigneeMenuPanelRef, e.target)) {
        setAssigneeMenuOpen(false);
      }
      if (isOutside(activityMenuRef, activityMenuPanelRef, e.target)) {
        setActivityMenuOpen(false);
      }
      if (isOutside(boardMenuRef, boardMenuPanelRef, e.target)) {
        setBoardMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  useEffect(() => {
    if (!selectedCard?.cardId) {
      setCardComments([]);
      return;
    }

    const cached = boardData?.commentsByCard?.[selectedCard.cardId];
    if (cached?.length) {
      setCardComments(cached);
    }

    let cancelled = false;
    fetchCardComments(selectedCard.cardId)
      .then((comments) => {
        if (!cancelled) setCardComments(comments);
      })
      .catch(() => {
        if (!cancelled && !cached?.length) setCardComments([]);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedCard?.cardId, boardData?.commentsByCard]);

  const handleAddCard = async (listId, title, position) => {
    try {
      await createCardApi(listId, { title, position });
      showSuccessToast("Card added.");
      await loadBoard(true);
    } catch (error) {
      const msg = error?.response?.data?.error?.message || error?.message || "Failed to add card.";
      showErrorToast(msg);
      throw error;
    }
  };

  const handleAddList = async ({ name, visibility, memberIds, addCardMemberIds }) => {
    if (!name?.trim() || addingList) return;

    setAddingList(true);
    try {
      const position = boardData?.lists?.length ?? 0;
      await createListApi(boardId, {
        name: name.trim(),
        position,
        visibility,
        memberIds,
        addCardMemberIds,
      });
      showSuccessToast("List added.");
      setIsAddingList(false);
      await loadBoard(true);
    } catch (error) {
      const msg = error?.response?.data?.error?.message || error?.message || "Failed to add list.";
      showErrorToast(msg);
    } finally {
      setAddingList(false);
    }
  };

  const handleUpdateList = async (listId, payload) => {
    try {
      await updateListApi(listId, payload);
      showSuccessToast("List updated.");
      await loadBoard(true);
    } catch (error) {
      const msg = error?.response?.data?.error?.message || error?.message || "Failed to update list.";
      showErrorToast(msg);
      throw error;
    }
  };

  const canEditListSettings = useCallback(
    (list) => {
      const currentUserId = Number(user?.id);
      if (!currentUserId || !list) return false;
      if (canUpdate) return true;
      if (Number(boardData?.board?.ownerId) === currentUserId) return true;
      return list.ownerId && Number(list.ownerId) === currentUserId;
    },
    [user?.id, canUpdate, boardData?.board?.ownerId]
  );

  const handleCardUpdate = async (cardId, patch) => {
    await updateCardApi(cardId, patch);
    const data = await loadBoard(true);
    if (data && selectedCard?.cardId === cardId) {
      const updated = data.cards.find((c) => c.cardId === cardId);
      if (updated) setSelectedCard(updated);
    }
  };

  const handleCommentAdd = async (cardId, body) => {
    await addCardCommentApi(cardId, body);
    const comments = await fetchCardComments(cardId);
    setCardComments(comments);
    await loadBoard(true);
  };

  const handleAttachmentUpload = useCallback(
    async (cardId, file) => {
      await uploadCardAttachmentApi(cardId, file);
      const data = await loadBoard(true);
      if (data && selectedCard?.cardId === cardId) {
        const updated = data.cards.find((c) => c.cardId === cardId);
        if (updated) setSelectedCard(updated);
      }
    },
    [loadBoard, selectedCard?.cardId]
  );

  const attachmentHandlers = useMemo(
    () => ({
      fetchAttachments: fetchCardAttachments,
      upload: handleAttachmentUpload,
    }),
    [handleAttachmentUpload]
  );

  const findListByDroppableId = useCallback(
    (droppableId) =>
      boardData?.lists?.find((list) => String(list.listId) === String(droppableId)) || null,
    [boardData?.lists]
  );

  const handleCardClick = (card) => {
    if (blockCardClickRef.current) return;
    setSelectedCard(card);
  };

  const handleCloseModal = () => {
    setSelectedCard(null);
    if (searchParams.get("card")) {
      searchParams.delete("card");
      setSearchParams(searchParams, { replace: true });
    }
  };

  const handleCardDelete = async (cardId) => {
    await deleteCardApi(cardId);
    showSuccessToast("Card deleted.");
    setSelectedCard(null);
    if (searchParams.get("card")) {
      searchParams.delete("card");
      setSearchParams(searchParams, { replace: true });
    }
    await loadBoard(true);
  };

  const handleDeleteBoard = async () => {
    if (!boardId || deletingBoard) return;
    setDeletingBoard(true);
    try {
      await deleteBoardApi(boardId);
      showSuccessToast("Board deleted.");
      navigate("/project-management");
    } catch (error) {
      const msg =
        error?.response?.data?.error?.message || error?.message || "Failed to delete board.";
      showErrorToast(msg);
    } finally {
      setDeletingBoard(false);
    }
  };

  const onDragStart = () => {
    document.body.classList.add("pb-dnd-active");

    const onPointerMove = (e) => {
      const point = e.touches?.[0] || e;
      if (point?.clientX != null) {
        dragPointerRef.current.x = point.clientX;
      }
    };

    const tickDragScroll = () => {
      const container = boardCanvasRef.current;
      const x = dragPointerRef.current.x;
      if (container && x) {
        const rect = container.getBoundingClientRect();
        const edge = 80;
        const maxScroll = container.scrollWidth - container.clientWidth;
        if (maxScroll > 0) {
          let nextScroll = container.scrollLeft;
          if (x < rect.left + edge) {
            const intensity = (rect.left + edge - x) / edge;
            nextScroll -= Math.ceil(6 + 14 * intensity);
          } else if (x > rect.right - edge) {
            const intensity = (x - (rect.right - edge)) / edge;
            nextScroll += Math.ceil(6 + 14 * intensity);
          }
          container.scrollLeft = Math.max(0, Math.min(maxScroll, nextScroll));
        }
      }
      dragScrollRafRef.current = requestAnimationFrame(tickDragScroll);
    };

    window.addEventListener("mousemove", onPointerMove);
    window.addEventListener("touchmove", onPointerMove, { passive: true });
    dragScrollRafRef.current = requestAnimationFrame(tickDragScroll);

    dragPointerRef.current.cleanup = () => {
      window.removeEventListener("mousemove", onPointerMove);
      window.removeEventListener("touchmove", onPointerMove);
      if (dragScrollRafRef.current) {
        cancelAnimationFrame(dragScrollRafRef.current);
        dragScrollRafRef.current = null;
      }
    };
  };

  const stopDragScroll = () => {
    dragPointerRef.current.cleanup?.();
    dragPointerRef.current.cleanup = null;
  };

  const executeCardMove = useCallback(async (result, moveExtras = null) => {
    if (!boardData?.cards) return;

    const prevCards = boardData.cards;
    boardSnapshotRef.current = prevCards;

    let nextCards = applyCardDrag(prevCards, result);
    if (!nextCards) return;

    // Optimistic auto-assign when dragging into In-Progress (mirrors backend).
    const destList = boardData.lists?.find(
      (l) => String(l.listId) === String(result.destination?.droppableId)
    );
    const actorId = Number(user?.id || user?.userId);
    if (isInProgressList(destList) && actorId > 0) {
      nextCards = nextCards.map((card) => {
        if (card.cardId !== result.draggableId) return card;
        const ids = (card.assigneeIds || []).map(Number).filter((id) => id > 0);
        if (ids.includes(actorId)) return card;
        const selfMember =
          (boardData.members || []).find((m) => Number(m.id || m.userId) === actorId)
          || {
            id: actorId,
            name: user?.name || [user?.firstname, user?.lastname].filter(Boolean).join(" ") || "You",
            profileImage: user?.profileImage || null,
          };
        return {
          ...card,
          assigneeIds: [...ids, actorId],
          assignees: [...(card.assignees || []).filter((a) => Number(a.id || a.userId) !== actorId), selfMember],
        };
      });
    }

    setBoardData((prev) => ({ ...prev, cards: nextCards }));

    try {
      const payload = buildMovePayload(nextCards, result);
      if (moveExtras?.actualHours != null) {
        payload.actualHours = moveExtras.actualHours;
      }
      if (moveExtras && "actualHoursOverReason" in moveExtras) {
        payload.actualHoursOverReason = moveExtras.actualHoursOverReason;
      }
      await moveCardApi(result.draggableId, payload);
      await loadBoard(true);
    } catch (error) {
      setBoardData((prev) => ({ ...prev, cards: boardSnapshotRef.current }));
      const msg =
        error?.response?.data?.error?.message || error?.message || "Failed to move card.";
      showErrorToast(msg);
      throw error;
    }
  }, [boardData, loadBoard, user]);

  const handleEstimatedHoursCancel = () => {
    if (savingEstimatedHours) return;
    setEstimatedHoursModalOpen(false);
    setPendingCardMove(null);
  };

  const handleEstimatedHoursConfirm = async (estimatedHours) => {
    if (!pendingCardMove) return;
    const cardId = pendingCardMove.draggableId;
    const sourceList = findListByDroppableId(pendingCardMove.source.droppableId);
    const destList = findListByDroppableId(pendingCardMove.destination.droppableId);

    setSavingEstimatedHours(true);
    try {
      await updateCardApi(cardId, { estimatedHours });
      setBoardData((prev) => {
        if (!prev?.cards) return prev;
        return {
          ...prev,
          cards: prev.cards.map((c) =>
            c.cardId === cardId ? { ...c, estimatedHours } : c
          ),
        };
      });
      setEstimatedHoursModalOpen(false);

      if (requiresActualHoursForMove(destList, sourceList)) {
        setActualHoursModalOpen(true);
        return;
      }

      if (
        pendingCardMove.source.droppableId !== pendingCardMove.destination.droppableId
        || pendingCardMove.source.index !== pendingCardMove.destination.index
      ) {
        blockCardClickRef.current = true;
        window.setTimeout(() => {
          blockCardClickRef.current = false;
        }, 100);
      }

      await executeCardMove(pendingCardMove);
      setPendingCardMove(null);
    } catch (error) {
      const msg =
        error?.response?.data?.error?.message || error?.message || "Failed to save estimated hours.";
      showErrorToast(msg);
    } finally {
      setSavingEstimatedHours(false);
    }
  };

  const handleActualHoursConfirm = async ({ actualHours, actualHoursOverReason }) => {
    if (!pendingCardMove) return;
    setMovingCard(true);
    try {
      await executeCardMove(pendingCardMove, { actualHours, actualHoursOverReason });
      setActualHoursModalOpen(false);
      setPendingCardMove(null);
    } finally {
      setMovingCard(false);
    }
  };

  const handleActualHoursCancel = () => {
    if (movingCard) return;
    setActualHoursModalOpen(false);
    setPendingCardMove(null);
  };

  const onDragEnd = async (result) => {
    stopDragScroll();
    document.body.classList.remove("pb-dnd-active");

    if (!boardData) return;
    const { destination, source, type } = result;
    if (!destination) return;

    if (type === "LIST") {
      if (!canUpdate) return;
      if (source.index === destination.index) return;

      const prevLists = boardData.lists;
      listsSnapshotRef.current = prevLists;

      const nextLists = applyListDrag(prevLists, result);
      if (!nextLists) return;

      setBoardData((prev) => ({ ...prev, lists: nextLists }));

      try {
        const payload = buildListMovePayload(nextLists);
        await moveBoardListsApi(boardId, payload);
      } catch (error) {
        setBoardData((prev) => ({ ...prev, lists: listsSnapshotRef.current }));
        const msg =
          error?.response?.data?.error?.message || error?.message || "Failed to reorder lists.";
        showErrorToast(msg);
      }
      return;
    }

    if (type === "CARD") {
      if (!boardData.cards) return;

      const sourceList = findListByDroppableId(source.droppableId);
      const destList = findListByDroppableId(destination.droppableId);

      if (!canDragCardInList(user, sourceList)) {
        showErrorToast("You cannot move cards in the Completed list.");
        return;
      }

      if (!canEmployeeMoveCardBetweenLists(user, sourceList, destList)) {
        showErrorToast("You cannot move cards in Completed.");
        return;
      }

      const card = boardData.cards.find((c) => c.cardId === result.draggableId);
      if (!card) return;

      const backlogValidation = validateCardMove({ sourceList, destList });
      if (!backlogValidation.ok) {
        showErrorToast(backlogValidation.message);
        return;
      }

      if (missingEstimatedHoursForMove(sourceList, destList, card)) {
        setPendingCardMove(result);
        setEstimatedHoursModalOpen(true);
        return;
      }

      const moveValidation = validateEmployeeCardMove({
        user,
        card,
        sourceList,
        destList,
      });
      if (!moveValidation.ok) {
        showErrorToast(moveValidation.message);
        return;
      }

      if (requiresActualHoursForMove(destList, sourceList)) {
        setPendingCardMove(result);
        setActualHoursModalOpen(true);
        return;
      }

      if (
        source.droppableId !== destination.droppableId ||
        source.index !== destination.index
      ) {
        blockCardClickRef.current = true;
        window.setTimeout(() => {
          blockCardClickRef.current = false;
        }, 100);
      }

      await executeCardMove(result);
    }
  };

  const boardFilterLabels = useMemo(
    () => [...BOARD_LABELS, ...getCustomLabels(boardId)],
    [boardId]
  );

  const filteredCards = useMemo(() => {
    if (!boardData?.cards) return [];
    const hasFilter = searchQuery || labelFilter || assigneeFilter || dueFilter;
    if (!hasFilter) return null;
    return filterBoardCards(boardData.cards, {
      search: searchQuery,
      labelId: labelFilter,
      assigneeId: assigneeFilter,
      dueFilter,
    }).map((c) => c.cardId);
  }, [boardData, searchQuery, labelFilter, assigneeFilter, dueFilter]);

  const selectedListName = useMemo(() => {
    if (!selectedCard || !boardData) return "";
    return boardData.lists.find((l) => l.listId === selectedCard.listId)?.name || "";
  }, [selectedCard, boardData]);

  const selectedCardActivity = useMemo(() => {
    if (!selectedCard?.cardId || !boardData?.activity) return [];
    return boardData.activity.filter((item) => item.cardId === selectedCard.cardId);
  }, [selectedCard?.cardId, boardData?.activity]);

  if (loading) {
    return (
      <div className="project-board-view project-board-view--loading">
        <Loader />
      </div>
    );
  }

  if (!boardData?.board) {
    return (
      <div className="pb-page project-board-view--missing">
        <div className="card-border card-body">
          <BoardEmptyState
            title="Board not found"
            description="This board may have been removed or you do not have access."
            actionLabel="Back to boards"
            onAction={() => navigate("/project-management")}
          />
        </div>
      </div>
    );
  }

  const { board, lists, cards, members, activity } = boardData;
  const sortedLists = [...lists].sort((a, b) => a.position - b.position);
  const hasActiveFilter = Boolean(searchQuery || labelFilter || assigneeFilter || dueFilter);

  return (
    <div
      className="pb-board project-board-view"
      style={{
        backgroundColor: board.backgroundColor,
        backgroundImage: board.backgroundUrl ? `url(${board.backgroundUrl})` : undefined,
      }}
    >
      <header className="pb-board__header">
        <div>
          <p className="project-board-view__project">{board.projectName}</p>
          <h1 className="pb-board__title">{board.name}</h1>
        </div>
        <div className="pb-board__toolbar">
          <BoardAvatarStack members={members} max={4} size="sm" />
          {(canUpdate || canDeleteBoard) && (
            <div
              className={`project-board-view__activity-wrap ${boardMenuOpen ? "project-board-view__activity-wrap--open" : ""}`}
              ref={boardMenuRef}
            >
              <BoardIconButton
                icon="⚙"
                label="Board settings"
                variant="ghost"
                className={boardMenuOpen ? "project-board-view__activity-btn--active" : ""}
                onClick={() => setBoardMenuOpen((open) => !open)}
              />
              {boardMenuOpen && (
                <BoardDropdownPortal
                  open={boardMenuOpen}
                  anchorRef={boardMenuRef}
                  menuRef={boardMenuPanelRef}
                  placement="bottom-end"
                  popoverWidth={200}
                  className="project-board-view__board-menu"
                >
                  {canUpdate && (
                    <button
                      type="button"
                      className="project-board-view__board-menu-item"
                      onClick={() => {
                        setBoardMenuOpen(false);
                        setEditBoardOpen(true);
                      }}
                    >
                      Edit board
                    </button>
                  )}
                  {canDeleteBoard && (
                    <button
                      type="button"
                      className="project-board-view__board-menu-item project-board-view__board-menu-item--danger"
                      onClick={() => {
                        setBoardMenuOpen(false);
                        setDeleteBoardOpen(true);
                      }}
                    >
                      Delete board
                    </button>
                  )}
                </BoardDropdownPortal>
              )}
            </div>
          )}
          <div
            className={`project-board-view__activity-wrap ${activityMenuOpen ? "project-board-view__activity-wrap--open" : ""}`}
            ref={activityMenuRef}
          >
            <BoardIconButton
              icon="🕐"
              label="Show recent activity"
              variant="ghost"
              className={activityMenuOpen ? "project-board-view__activity-btn--active" : ""}
              onClick={() => setActivityMenuOpen((open) => !open)}
            />
            {activityMenuOpen && (
              <BoardDropdownPortal
                open={activityMenuOpen}
                anchorRef={activityMenuRef}
                menuRef={activityMenuPanelRef}
                placement="bottom-end"
                popoverWidth={360}
                className="project-board-view__activity-panel"
              >
                <div className="project-board-view__activity-panel-header">
                  <h3 className="project-board-view__activity-panel-title">Recent activity</h3>
                  <button
                    type="button"
                    className="project-board-view__activity-panel-close"
                    onClick={() => setActivityMenuOpen(false)}
                    aria-label="Close"
                  >
                    ✕
                  </button>
                </div>
                <div className="project-board-view__activity-panel-body">
                  <BoardActivityFeed
                    activity={activity}
                    members={members}
                    limit={100}
                    onAttachmentPreview={setPreviewAttachment}
                  />
                </div>
              </BoardDropdownPortal>
            )}
          </div>
          <BoardButton variant="ghost" onClick={() => navigate("/project-management")}>
            ← Boards
          </BoardButton>
        </div>
      </header>

      <div className="pb-toolbar pb-toolbar--on-board">
        <BoardSearchInput
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder="Search cards..."
        />

        <div
          className={`project-board-view__filter-wrap ${labelMenuOpen ? "project-board-view__filter-wrap--open" : ""}`}
          ref={labelMenuRef}
        >
          <BoardChip active={!!labelFilter} onClick={() => setLabelMenuOpen((o) => !o)}>
            {labelFilter
              ? boardFilterLabels.find((l) => l.id === labelFilter)?.name
              : "Labels"}
          </BoardChip>
          {labelMenuOpen && (
            <BoardDropdownPortal
              open={labelMenuOpen}
              anchorRef={labelMenuRef}
              menuRef={labelMenuPanelRef}
              className="project-board-view__filter-menu"
            >
              <button type="button" onClick={() => { setLabelFilter(""); setLabelMenuOpen(false); }}>
                All labels
              </button>
              {boardFilterLabels.map((l) => (
                <button
                  key={l.id}
                  type="button"
                  className={labelFilter === l.id ? "is-active" : ""}
                  onClick={() => { setLabelFilter(l.id); setLabelMenuOpen(false); }}
                >
                  <span className="project-board-view__label-dot" style={{ background: l.color }} />
                  {l.name}
                </button>
              ))}
            </BoardDropdownPortal>
          )}
        </div>

        {members.length > 0 && (
          <div
            className={`project-board-view__filter-wrap ${assigneeMenuOpen ? "project-board-view__filter-wrap--open" : ""}`}
            ref={assigneeMenuRef}
          >
            <BoardChip active={!!assigneeFilter} onClick={() => setAssigneeMenuOpen((o) => !o)}>
              {assigneeFilter
                ? members.find((m) => String(m.id || m.userId) === assigneeFilter)?.name || "Assignee"
                : "Assignee"}
            </BoardChip>
            {assigneeMenuOpen && (
              <BoardDropdownPortal
                open={assigneeMenuOpen}
                anchorRef={assigneeMenuRef}
                menuRef={assigneeMenuPanelRef}
                className="project-board-view__filter-menu"
              >
                <button type="button" onClick={() => { setAssigneeFilter(""); setAssigneeMenuOpen(false); }}>
                  All assignees
                </button>
                {members.map((m) => (
                  <button
                    key={m.id || m.userId}
                    type="button"
                    className={assigneeFilter === String(m.id || m.userId) ? "is-active" : ""}
                    onClick={() => {
                      setAssigneeFilter(String(m.id || m.userId));
                      setAssigneeMenuOpen(false);
                    }}
                  >
                    {m.name}
                  </button>
                ))}
              </BoardDropdownPortal>
            )}
          </div>
        )}

        <div
          className={`project-board-view__filter-wrap ${dueMenuOpen ? "project-board-view__filter-wrap--open" : ""}`}
          ref={dueMenuRef}
        >
          <BoardChip active={!!dueFilter} onClick={() => setDueMenuOpen((o) => !o)}>
            {dueFilter
              ? BOARD_DUE_DATE_FILTERS.find((f) => f.value === dueFilter)?.label
              : "Due date"}
          </BoardChip>
          {dueMenuOpen && (
            <BoardDropdownPortal
              open={dueMenuOpen}
              anchorRef={dueMenuRef}
              menuRef={dueMenuPanelRef}
              className="project-board-view__filter-menu"
            >
              {BOARD_DUE_DATE_FILTERS.map((f) => (
                <button
                  key={f.value || "all"}
                  type="button"
                  className={dueFilter === f.value ? "is-active" : ""}
                  onClick={() => { setDueFilter(f.value); setDueMenuOpen(false); }}
                >
                  {f.label}
                </button>
              ))}
            </BoardDropdownPortal>
          )}
        </div>

        {hasActiveFilter && (
          <BoardChip
            onClick={() => {
              setSearchQuery("");
              setLabelFilter("");
              setAssigneeFilter("");
              setDueFilter("");
            }}
          >
            Clear filters
          </BoardChip>
        )}
      </div>

      <DragDropContext onDragStart={onDragStart} onDragEnd={onDragEnd}>
        <Droppable
          droppableId={getBoardListsDroppableId(boardId)}
          type="LIST"
          direction="horizontal"
        >
          {(listDropProvided) => (
            <div
              ref={(node) => {
                listDropProvided.innerRef(node);
                boardCanvasRef.current = node;
              }}
              {...listDropProvided.droppableProps}
              className="pb-board__canvas"
            >
              {sortedLists.map((list, index) => (
                <Draggable
                  key={String(list.listId)}
                  draggableId={String(list.listId)}
                  index={index}
                  isDragDisabled={!canUpdate || hasActiveFilter}
                >
                  {(listDragProvided, listDragSnapshot) => (
                    <div
                      ref={listDragProvided.innerRef}
                      {...listDragProvided.draggableProps}
                      className={`pb-list-wrap ${listDragSnapshot.isDragging ? "pb-list-wrap--dragging" : ""}`}
                      style={listDragProvided.draggableProps.style}
                    >
                      <BoardListColumn
                        list={list}
                        boardId={boardId}
                        allCards={cards}
                        filteredCardIds={hasActiveFilter ? filteredCards : null}
                        onCardClick={handleCardClick}
                        canCreate={canUserAddCardToList(user, list, board?.ownerId)}
                        canDrag={canDragCardInList(user, list)}
                        canDragList={canUpdate && !hasActiveFilter}
                        canEditList={canEditListSettings(list)}
                        boardMembers={members}
                        listDragHandleProps={listDragProvided.dragHandleProps}
                        onAddCard={handleAddCard}
                        onUpdateList={handleUpdateList}
                      />
                    </div>
                  )}
                </Draggable>
              ))}
              {listDropProvided.placeholder}

              {canCreateList && (
                <div className="pb-list project-board-view__add-list">
                  {isAddingList ? (
                    <BoardCreateListForm
                      submitting={addingList}
                      boardMembers={members}
                      onSubmit={handleAddList}
                      onCancel={() => setIsAddingList(false)}
                    />
                  ) : (
                    <BoardButton variant="ghost-dark" onClick={() => setIsAddingList(true)}>
                      + Add another list
                    </BoardButton>
                  )}
                </div>
              )}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      {selectedCard && (
        <BoardCardModal
          card={selectedCard}
          boardId={boardId}
          listName={selectedListName}
          comments={cardComments}
          cardActivity={selectedCardActivity}
          boardMembers={members}
          canManage={canManageCard}
          canEdit={canEditCard}
          onClose={handleCloseModal}
          onUpdate={handleCardUpdate}
          onDelete={canManageCard ? handleCardDelete : undefined}
          onCommentAdd={handleCommentAdd}
          onAttachmentChange={attachmentHandlers}
        />
      )}

      <BoardCreateModal
        open={editBoardOpen}
        board={board}
        onClose={() => setEditBoardOpen(false)}
        onUpdated={async () => {
          setEditBoardOpen(false);
          await loadBoard(true);
        }}
      />

      <ConfirmModal
        isOpen={deleteBoardOpen}
        onClose={() => !deletingBoard && setDeleteBoardOpen(false)}
        onConfirm={handleDeleteBoard}
        title="Delete board?"
        content={`Delete "${board.name}"? Lists and cards on this board will no longer be available.`}
        confirmText="Delete board"
        confirmLoading={deletingBoard}
      />

      {previewAttachment && (
        <BoardAttachmentViewer
          attachment={previewAttachment}
          onClose={() => setPreviewAttachment(null)}
        />
      )}

      <BoardEstimatedHoursModal
        open={estimatedHoursModalOpen}
        cardTitle={
          pendingCardMove
            ? boardData?.cards?.find((c) => c.cardId === pendingCardMove.draggableId)?.title
            : ""
        }
        sourceListName={
          pendingCardMove
            ? findListByDroppableId(pendingCardMove.source.droppableId)?.name || "To Do"
            : "To Do"
        }
        onClose={handleEstimatedHoursCancel}
        onConfirm={handleEstimatedHoursConfirm}
        loading={savingEstimatedHours}
      />

      <BoardActualHoursModal
        open={actualHoursModalOpen}
        cardTitle={
          pendingCardMove
            ? boardData?.cards?.find((c) => c.cardId === pendingCardMove.draggableId)?.title
            : ""
        }
        estimatedHours={
          pendingCardMove
            ? boardData?.cards?.find((c) => c.cardId === pendingCardMove.draggableId)?.estimatedHours
            : null
        }
        onClose={handleActualHoursCancel}
        onConfirm={handleActualHoursConfirm}
        loading={movingCard}
      />
    </div>
  );
};

export default ProjectBoardPage;
